import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Film, Calendar, Zap, Download, Play, Clock, LogIn, X, ImageIcon, ArrowRight, Eye, RefreshCw, Volume2, Pause, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VideoPreviewModal({ video, streamUrl, onClose }) {
  if (!video) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white hover:text-[#FFB238] transition-colors"
          data-testid="close-preview-button"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="bg-[#0F1419] rounded-2xl overflow-hidden border border-[#222D4A]">
          <video
            src={streamUrl}
            controls
            autoPlay
            className="w-full aspect-video"
            data-testid="video-player"
          >
            Your browser does not support the video tag.
          </video>
          <div className="p-4">
            <p className="text-gray-400 text-sm">{video.prompt_text}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MyVideos() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [videos, setVideos] = useState([]);
  const [images, setImages] = useState([]);
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [galleryTab, setGalleryTab] = useState(() => {
    // Deep-link support: /my-gallery can be visited from the Transform or
    // Image tools which pass state: { tab: 'images' }. Falls back to 'videos'.
    const t = location.state?.tab;
    return ['videos', 'images', 'sounds'].includes(t) ? t : 'videos';
  });
  const [sendingToVideo, setSendingToVideo] = useState(null);
  const [loopingVideoId, setLoopingVideoId] = useState(null);
  const [upscalingVideoId, setUpscalingVideoId] = useState(null);

  // Pagination state per tab
  const PAGE_SIZE = 9;
  const [videosHasMore, setVideosHasMore] = useState(true);
  const [imagesHasMore, setImagesHasMore] = useState(true);
  const [soundsHasMore, setSoundsHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);

  // Fetch a page of items for a specific tab
  const fetchPage = useCallback(async (type, skip = 0) => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return [];
    const endpoints = {
      videos: `${API}/videos?limit=${PAGE_SIZE}&skip=${skip}`,
      images: `${API}/images?limit=${PAGE_SIZE}&skip=${skip}`,
      sounds: `${API}/sounds?limit=${PAGE_SIZE}&skip=${skip}`,
    };
    const res = await axios.get(endpoints[type], { headers: { Authorization: `Bearer ${currentToken}` } });
    return Array.isArray(res.data) ? res.data : [];
  }, []);

  // Initial load: fetch first page of active tab
  useEffect(() => {
    if (!user) return; // Don't touch loading state — let auth handle it
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchPage(galleryTab, 0);
        if (cancelled) return;
        if (galleryTab === 'videos') { setVideos(data); setVideosHasMore(data.length >= PAGE_SIZE); }
        else if (galleryTab === 'images') { setImages(data); setImagesHasMore(data.length >= PAGE_SIZE); }
        else { setSounds(data); setSoundsHasMore(data.length >= PAGE_SIZE); }
      } catch (e) { console.error('Gallery initial load failed:', e); }
      if (!cancelled) setLoading(false);
    };
    load();
    // Poll for processing items every 8s
    const interval = setInterval(async () => {
      if (cancelled) return;
      try {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) return;
        const res = await axios.get(
          `${API}/${galleryTab}?limit=${PAGE_SIZE}&skip=0`,
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );
        const freshData = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          const setters = { videos: setVideos, images: setImages, sounds: setSounds };
          setters[galleryTab](prev => {
            if (prev.length <= PAGE_SIZE) return freshData;
            return [...freshData, ...prev.slice(PAGE_SIZE)];
          });
        }
      } catch (e) { console.error('Gallery polling failed:', e); }
    }, 8000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, galleryTab]);

  // Load more items (infinite scroll trigger)
  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const currentItems = galleryTab === 'videos' ? videos : galleryTab === 'images' ? images : sounds;
    const hasMore = galleryTab === 'videos' ? videosHasMore : galleryTab === 'images' ? imagesHasMore : soundsHasMore;
    if (!hasMore) return;

    setLoadingMore(true);
    try {
      const data = await fetchPage(galleryTab, currentItems.length);
      if (data.length < PAGE_SIZE) {
        if (galleryTab === 'videos') setVideosHasMore(false);
        else if (galleryTab === 'images') setImagesHasMore(false);
        else setSoundsHasMore(false);
      }
      if (galleryTab === 'videos') setVideos(prev => [...prev, ...data]);
      else if (galleryTab === 'images') setImages(prev => [...prev, ...data]);
      else setSounds(prev => [...prev, ...data]);
    } catch (e) { console.error('Load more failed:', e); }
    setLoadingMore(false);
  }, [galleryTab, videos, images, sounds, videosHasMore, imagesHasMore, soundsHasMore, loadingMore, fetchPage]);

  // Intersection observer for infinite scroll
  const lastItemRef = useCallback((node) => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMore();
    }, { threshold: 0.1 });
    if (node) observerRef.current.observe(node);
  }, [loadMore, loadingMore]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const GALLERY_RETENTION_DAYS = 90;

  const getTimeRemaining = (createdAt) => {
    const created = new Date(createdAt);
    const expiry = new Date(created.getTime() + GALLERY_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    // Only surface the countdown within 30 days of expiry; hide it otherwise.
    if (days > 30) return null;
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const getVideoStreamUrl = (video) => {
    return `${API}/videos/${video.id}/stream?token=${token}`;
  };

  const getVideoThumbnailUrl = (video) => {
    return `${API}/videos/${video.id}/thumbnail?token=${token}`;
  };

  const getImageViewUrl = (image) => {
    return `${API}/images/${image.id}/view?token=${token}`;
  };

  const getImageThumbnailUrl = (image) => {
    return `${API}/images/${image.id}/thumbnail?token=${token}`;
  };

  const getImageTimeRemaining = (createdAt) => {
    const created = new Date(createdAt);
    const expiry = new Date(created.getTime() + GALLERY_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diff = expiry - now;
    if (diff <= 0) return 'Expired';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    // Only surface the countdown within 30 days of expiry; hide it otherwise.
    if (days > 30) return null;
    if (days > 0) return `${days}d left`;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return `${hours}h left`;
  };

  const getSoundStreamUrl = (sound) => {
    return `${API}/sounds/${sound.id}/stream?token=${token}`;
  };

  const [playingSound, setPlayingSound] = useState(null);
  const audioRefs = React.useRef({});

  const handleSendToVideo = async (image) => {
    setSendingToVideo(image.id);
    try {
      const res = await axios.post(
        `${API}/images/${image.id}/send-to-video`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const previewUrl = getImageViewUrl(image);
      navigate('/', {
        state: {
          fromImage: true,
          imageId: res.data.image_id,
          imagePreviewUrl: previewUrl,
          fileName: res.data.filename
        }
      });
    } catch (error) {
      toast.error('Failed to prepare image for video generation');
    } finally {
      setSendingToVideo(null);
    }
  };

  const [sendingToTransform, setSendingToTransform] = useState(null);
  const handleSendToTransform = async (image) => {
    setSendingToTransform(image.id);
    try {
      const res = await axios.post(
        `${API}/images/${image.id}/send-to-transform`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const previewUrl = getImageViewUrl(image);
      navigate('/transform', {
        state: {
          fromTransformImage: true,
          imageId: res.data.image_id,
          imagePreviewUrl: previewUrl,
          fileName: res.data.filename,
        }
      });
    } catch (error) {
      toast.error('Failed to prepare image for transformation');
    } finally {
      setSendingToTransform(null);
    }
  };

  const handleMakeItLoop = async (video) => {
    // Navigate to Generate/Loop with the video stream URL — file will be fetched on submission
    navigate('/', {
      state: {
        fromLoop: true,
        loopVideoId: video.id,
        loopVideoName: `video_${video.id.substring(0, 8)}.mp4`
      }
    });
  };

  const handleUpscale4K = async (video) => {
    if (upscalingVideoId) return;
    setUpscalingVideoId(video.id);
    try {
      const res = await axios.post(
        `${API}/videos/upscale-4k-from-gallery?video_id=${encodeURIComponent(video.id)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('4K upscale started! It will appear in your gallery shortly.');
      // Optimistic: insert the new processing record at the top
      if (res.data?.video_id) {
        setVideos(prev => [
          {
            id: res.data.video_id,
            status: 'Processing',
            video_type: 'upscaled',
            upscaled: true,
            prompt_text: '4K Upscale',
            created_at: new Date().toISOString(),
            credit_cost: res.data.credit_cost ?? null,
          },
          ...prev,
        ]);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start 4K upscale.';
      toast.error(msg);
    } finally {
      setUpscalingVideoId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      Processing: { color: 'bg-yellow-500', text: 'Processing' },
      Completed: { color: 'bg-green-500', text: 'Completed' },
      Failed: { color: 'bg-red-500', text: 'Failed' }
    };
    const badge = config[status] || config.Processing;
    return (
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${badge.color} w-fit`} data-testid={`status-badge-${status.toLowerCase()}`}>
        <span className="text-xs font-medium text-white">{badge.text}</span>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {/* Video Preview Modal */}
      {previewVideo && (
        <VideoPreviewModal
          video={previewVideo}
          streamUrl={getVideoStreamUrl(previewVideo)}
          onClose={() => setPreviewVideo(null)}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewImage(null)} data-testid="image-preview-modal">
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-[#FFB238] transition-colors"
              data-testid="close-image-preview-button"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="bg-[#0F1419] rounded-2xl overflow-hidden border border-[#222D4A]">
              <img
                src={getImageViewUrl(previewImage)}
                alt={previewImage.prompt}
                className="w-full max-h-[70vh] object-contain"
              />
              <div className="p-4 flex items-center justify-between">
                <p className="text-gray-400 text-sm flex-1 mr-4">
                  {previewImage.type === 'transformed'
                    ? (previewImage.user_description?.trim() || 'Transformed image')
                    : previewImage.prompt}
                </p>
                <a
                  href={`${API}/images/${previewImage.id}/download?token=${token}`}
                  download
                  className="px-4 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors flex items-center gap-2 no-underline flex-shrink-0"
                  data-testid="preview-image-download"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8" data-testid="my-gallery-header">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">My Gallery</h1>
        <p className="text-gray-300">
          All your generated content in one place. Videos, images, and sounds expire after 90 days.
        </p>
      </div>

      {/* Show login prompt if not authenticated (wait for auth check first) */}
      {authLoading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFB238] border-t-transparent mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading...</p>
        </div>
      ) : !user ? (
        <div className="max-w-2xl mx-auto text-center py-20" data-testid="login-prompt">
          <div className="bg-[#1A2035] rounded-2xl p-12 border border-[#222D4A]">
            <LogIn className="w-16 h-16 text-[#FFB238] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Sign In to View Your Gallery</h2>
            <p className="text-gray-300 mb-8">
              Create an account or sign in to access your personal gallery.
            </p>
            <button
              onClick={() => navigate('/account')}
              className="px-8 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors"
              data-testid="go-to-login-button"
            >
              Sign In / Sign Up
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFB238] border-t-transparent mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading gallery...</p>
        </div>
      ) : (
        <>
          {/* Gallery Tabs */}
          <div className="flex justify-center gap-4 mb-8" data-testid="gallery-tabs">
            <button
              onClick={() => setGalleryTab('videos')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all border-2 ${
                galleryTab === 'videos'
                  ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]'
                  : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
              }`}
              data-testid="gallery-tab-videos"
            >
              <Film className="w-4 h-4" />
              Videos
            </button>
            <button
              onClick={() => setGalleryTab('images')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all border-2 ${
                galleryTab === 'images'
                  ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]'
                  : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
              }`}
              data-testid="gallery-tab-images"
            >
              <ImageIcon className="w-4 h-4" />
              Images
            </button>
          </div>

          {/* Videos Tab */}
          {galleryTab === 'videos' && (
          <>
          {/* Videos Grid */}
          {videos.length === 0 ? (
            <div className="text-center py-20" data-testid="no-videos-message">
              <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No videos yet. Generate your first cozy video!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
              {videos.map((video, idx) => (
                <div
                  key={video.id}
                  ref={idx === videos.length - 1 && galleryTab === 'videos' ? lastItemRef : null}
                  className="bg-[#1A2035] rounded-2xl overflow-hidden border border-[#222D4A] hover:border-[#FFB238] transition-all"
                  data-testid={`video-card-${video.id}`}
                >
                  {/* Video Thumbnail */}
                  <div
                    className="aspect-video bg-[#0F1419] flex items-center justify-center cursor-pointer relative group"
                    onClick={() => video.status === 'Completed' && setPreviewVideo(video)}
                  >
                    {video.status === 'Processing' ? (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFB238] border-t-transparent mx-auto mb-3"></div>
                        <p className="text-gray-400 text-sm">{video.video_type === 'loop' ? 'Creating your loop...' : 'Generating your video...'}</p>
                        <p className="text-gray-500 text-xs mt-1">{video.video_type === 'loop' ? 'This may take 10-20 seconds' : 'This may take 3-5 minutes'}</p>
                      </div>
                    ) : video.status === 'Completed' ? (
                      <>
                        <img
                          src={getVideoThumbnailUrl(video)}
                          alt={video.prompt?.substring(0, 40)}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            // Fallback: load video poster if thumbnail not available
                            const vid = document.createElement('video');
                            vid.src = getVideoStreamUrl(video);
                            vid.className = e.target.className;
                            vid.muted = true;
                            vid.preload = 'metadata';
                            vid.onloadedmetadata = () => { vid.currentTime = 0.5; };
                            e.target.parentNode.replaceChild(vid, e.target);
                          }}
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 rounded-full bg-[#FFB238] flex items-center justify-center">
                            <Play className="w-8 h-8 text-[#0F1419] ml-1" fill="#0F1419" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <p className="text-red-400 font-medium">Generation Failed</p>
                        <p className="text-gray-500 text-xs mt-1">{video.error_message || 'Credits have been refunded'}</p>
                      </div>
                    )}
                  </div>

                  {/* Video Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(video.status)}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          video.video_type === 'loop'
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                            : video.video_type === 'rain_canvas'
                            ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        }`} data-testid={`video-type-badge-${video.id}`}>
                          {video.video_type === 'loop' ? 'Loop' : video.video_type === 'rain_canvas' ? 'Rain Canvas' : video.video_type === 'upscaled' || video.upscaled ? '4K Upscaled' : 'AI Generated'}
                        </span>
                      </div>
                      {getTimeRemaining(video.created_at) && (
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{getTimeRemaining(video.created_at)}</span>
                        </div>
                      )}
                    </div>

                    {video.credit_cost !== undefined && video.credit_cost !== null && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                        <Zap className="w-4 h-4 text-[#FFB238]" />
                        <span>{video.credit_cost === 0 ? 'Free' : `${video.credit_cost} credits used`}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(video.created_at)}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setPreviewVideo(video)}
                        disabled={video.status !== 'Completed'}
                        className="flex-1 px-4 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid={`preview-button-${video.id}`}
                      >
                        <Play className="w-4 h-4" />
                        <span>Preview</span>
                      </button>
                      {video.status === 'Completed' ? (
                        <a
                          href={`${API}/videos/${video.id}/download?token=${token}`}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] no-underline"
                          data-testid={`download-button-${video.id}`}
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      ) : (
                        <span
                          className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium opacity-30 cursor-not-allowed flex items-center justify-center gap-2 border border-[#2A3550]"
                          data-testid={`download-button-${video.id}`}
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </span>
                      )}
                    </div>
                    {video.status === 'Completed' && (
                      <button
                        onClick={() => handleMakeItLoop(video)}
                        disabled={loopingVideoId === video.id}
                        className="w-full mt-3 px-4 py-3 rounded-lg bg-[#1A2035] text-white font-medium hover:bg-[#222D4A] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] disabled:opacity-50"
                        data-testid={`loop-button-${video.id}`}
                      >
                        {loopingVideoId === video.id ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Preparing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 text-[#FFB238]" /> Make it Loop</>
                        )}
                      </button>
                    )}
                    {video.status === 'Completed' && !(video.video_type === 'upscaled' || video.upscaled) && (
                      <button
                        onClick={() => handleUpscale4K(video)}
                        disabled={upscalingVideoId === video.id}
                        className="w-full mt-3 px-4 py-3 rounded-lg bg-[#1A2035] text-white font-medium hover:bg-[#222D4A] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] disabled:opacity-50"
                        data-testid={`upscale-4k-button-${video.id}`}
                      >
                        {upscalingVideoId === video.id ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Starting...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 text-[#FFB238]" /> Upscale to 4k</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {galleryTab === 'videos' && loadingMore && (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-[#FFB238] animate-spin" /></div>
          )}
        </>
          )}

          {/* Images Tab */}
          {galleryTab === 'images' && (
            <>
              {images.length === 0 ? (
                <div className="text-center py-20" data-testid="no-images-message">
                  <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No images yet.</p>
                  <p className="text-gray-500 mt-2">Generate your first image from the Image Generator tab!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="images-grid">
                  {images.map((img, idx) => (
                    <div
                      key={img.id}
                      ref={idx === images.length - 1 && galleryTab === 'images' ? lastItemRef : null}
                      className="bg-[#1A2035] rounded-2xl overflow-hidden border border-[#222D4A] hover:border-[#FFB238] transition-all"
                      data-testid={`image-card-${img.id}`}
                    >
                      {/* Image Thumbnail */}
                      <div
                        className="aspect-video bg-[#0F1419] flex items-center justify-center cursor-pointer relative group"
                        onClick={() => img.status === 'Completed' && setPreviewImage(img)}
                      >
                        {img.status === 'Processing' ? (
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFB238] border-t-transparent mx-auto mb-3"></div>
                            <p className="text-gray-400 text-sm">Generating image...</p>
                            <p className="text-gray-500 text-xs mt-1">Usually takes 15-30 seconds</p>
                          </div>
                        ) : img.status === 'Completed' ? (
                          <>
                            <img
                              src={getImageThumbnailUrl(img)}
                              alt={img.prompt}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-16 h-16 rounded-full bg-[#FFB238] flex items-center justify-center">
                                <Eye className="w-8 h-8 text-[#0F1419]" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center">
                            <p className="text-red-400 font-medium">Generation Failed</p>
                            <p className="text-gray-500 text-xs mt-1">{img.error_message || 'Credits have been refunded'}</p>
                          </div>
                        )}
                      </div>

                      {/* Image Info */}
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getStatusBadge(img.status)}
                            {img.type === 'transformed' && (
                              <span
                                className="px-2 py-0.5 rounded-full text-xs font-medium border bg-[#FFB238]/15 text-[#FFB238] border-[#FFB238]/30"
                                data-testid={`image-transformed-badge-${img.id}`}
                              >
                                Transformed
                              </span>
                            )}
                          </div>
                          {getImageTimeRemaining(img.created_at) && (
                            <div className="flex items-center gap-1 text-gray-400 text-sm">
                              <Clock className="w-4 h-4" />
                              <span>{getImageTimeRemaining(img.created_at)}</span>
                            </div>
                          )}
                        </div>

                        <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                          {img.type === 'transformed'
                            ? (img.user_description?.trim() || 'Transformed image')
                            : img.prompt}
                        </p>

                        <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
                          <div className="flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5 text-[#FFB238]" />
                            <span>{img.credit_cost} credits</span>
                          </div>
                          <span>{img.resolution === '4k' ? '4K' : img.resolution}</span>
                          <span>{img.aspect_ratio}</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-3">
                            {img.status === 'Completed' ? (
                              <a
                                href={`${API}/images/${img.id}/download?token=${token}`}
                                download
                                onClick={(e) => e.stopPropagation()}
                                className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] no-underline"
                                data-testid={`image-download-${img.id}`}
                              >
                                <Download className="w-4 h-4" />
                                <span>Download</span>
                              </a>
                            ) : (
                              <span className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium opacity-30 cursor-not-allowed flex items-center justify-center gap-2 border border-[#2A3550]">
                                <Download className="w-4 h-4" />
                                <span>Download</span>
                              </span>
                            )}
                            {img.status === 'Completed' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSendToVideo(img); }}
                                disabled={sendingToVideo === img.id}
                                className="flex-1 px-4 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid={`image-animate-${img.id}`}
                                title="Send this image to the Video Generator"
                              >
                                {sendingToVideo === img.id ? (
                                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Preparing...</span></>
                                ) : (
                                  <><Film className="w-4 h-4" /><span>Animate</span></>
                                )}
                              </button>
                            )}
                          </div>
                          {img.status === 'Completed' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSendToTransform(img); }}
                              disabled={sendingToTransform === img.id}
                              className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] border border-[#2A3550] hover:border-[#FFB238]/50 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                              data-testid={`image-transform-${img.id}`}
                              title="Send this image to the Transform tool"
                            >
                              {sendingToTransform === img.id ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /><span>Preparing...</span></>
                              ) : (
                                <><Wand2 className="w-4 h-4 text-[#FFB238]" /><span>Transform</span></>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {galleryTab === 'images' && loadingMore && (
                <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-[#FFB238] animate-spin" /></div>
              )}
            </>
          )}

          {/* Sounds Tab */}
          {galleryTab === 'sounds' && (
            <>
              <div className="bg-purple-500/80 rounded-xl p-4 mb-8 flex items-center justify-between" data-testid="sound-deletion-notice">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-white" />
                  <p className="text-white font-medium">Sounds are automatically deleted 90 days after creation. Download anything you'd like to keep!</p>
                </div>
              </div>

              {sounds.length === 0 ? (
                <div className="text-center py-20" data-testid="no-sounds-message">
                  <Volume2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No sounds yet.</p>
                  <p className="text-gray-500 mt-2">Generate your first ambient sound from the Sound Effects tab!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sounds-grid">
                  {sounds.map((sound, idx) => (
                    <div
                      key={sound.id}
                      ref={idx === sounds.length - 1 && galleryTab === 'sounds' ? lastItemRef : null}
                      className="bg-[#1A2035] rounded-2xl overflow-hidden border border-[#222D4A] hover:border-[#FFB238] transition-all"
                      data-testid={`sound-card-${sound.id}`}
                    >
                      {/* Sound Visual / Player Area */}
                      <div className="aspect-[2/1] bg-[#0F1419] flex items-center justify-center relative">
                        {sound.status === 'Processing' ? (
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFB238] border-t-transparent mx-auto mb-3"></div>
                            <p className="text-gray-400 text-sm">Generating sound...</p>
                            <p className="text-gray-500 text-xs mt-1">Usually takes 30-60 seconds</p>
                          </div>
                        ) : sound.status === 'Completed' ? (
                          <div className="flex flex-col items-center gap-3 w-full px-6">
                            <button
                              onClick={() => {
                                const audioEl = audioRefs.current[sound.id];
                                if (!audioEl) return;
                                if (playingSound === sound.id) {
                                  audioEl.pause();
                                  setPlayingSound(null);
                                } else {
                                  // Pause any other playing audio
                                  Object.entries(audioRefs.current).forEach(([id, el]) => {
                                    if (id !== sound.id && el) el.pause();
                                  });
                                  audioEl.play().catch(() => {});
                                  setPlayingSound(sound.id);
                                }
                              }}
                              className="w-16 h-16 rounded-full bg-[#FFB238] flex items-center justify-center hover:bg-[#FFDA82] transition-colors"
                              data-testid={`play-sound-${sound.id}`}
                            >
                              {playingSound === sound.id ? (
                                <Pause className="w-7 h-7 text-[#0F1419]" fill="#0F1419" />
                              ) : (
                                <Play className="w-7 h-7 text-[#0F1419] ml-1" fill="#0F1419" />
                              )}
                            </button>
                            <audio
                              ref={(el) => { audioRefs.current[sound.id] = el; }}
                              src={getSoundStreamUrl(sound)}
                              loop
                              preload="none"
                              onEnded={() => setPlayingSound(null)}
                              onPause={() => { if (playingSound === sound.id) setPlayingSound(null); }}
                              data-testid={`audio-element-${sound.id}`}
                            />
                            <p className="text-gray-500 text-xs">Click to {playingSound === sound.id ? 'pause' : 'play'} (loops)</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <p className="text-red-400 font-medium">Generation Failed</p>
                            <p className="text-gray-500 text-xs mt-1">{sound.error_message || 'Credits have been refunded'}</p>
                          </div>
                        )}
                      </div>

                      {/* Sound Info */}
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          {getStatusBadge(sound.status)}
                          {getTimeRemaining(sound.created_at) && (
                            <div className="flex items-center gap-1 text-gray-400 text-sm">
                              <Clock className="w-4 h-4" />
                              <span>{getTimeRemaining(sound.created_at)}</span>
                            </div>
                          )}
                        </div>

                        <p className="text-gray-300 text-sm mb-3 line-clamp-2">{sound.prompt}</p>

                        <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
                          <div className="flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5 text-[#FFB238]" />
                            <span>{sound.credit_cost} credits</span>
                          </div>
                          <span>{sound.duration >= 60 ? (sound.duration >= 1200 ? '20 min' : sound.duration >= 300 ? '5 min' : '1 min') : `${sound.duration}s`}</span>
                        </div>

                        {/* Download Button */}
                        <div className="flex gap-3">
                          {sound.status === 'Completed' ? (
                            <a
                              href={`${API}/sounds/${sound.id}/download?token=${token}`}
                              download
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] no-underline"
                              data-testid={`sound-download-${sound.id}`}
                            >
                              <Download className="w-4 h-4" />
                              <span>Download MP3</span>
                            </a>
                          ) : (
                            <span className="flex-1 px-4 py-3 rounded-lg bg-[#222D4A] text-white font-medium opacity-30 cursor-not-allowed flex items-center justify-center gap-2 border border-[#2A3550]">
                              <Download className="w-4 h-4" />
                              <span>Download MP3</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {galleryTab === 'sounds' && loadingMore && (
                <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-[#FFB238] animate-spin" /></div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default MyVideos;
